import { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react'
import { useTranslation } from '../../i18n/LanguageContext'
import { useCrosswords, type Difficulty } from './useCrosswords'
import type { CrosswordPuzzle } from './crosswordsData'
import { activities as activitiesApi } from '../../api/client'
import { useTimer } from '../useTimer'
import { useGameCompletion } from '../../hooks/useGameCompletion'
import { useGameSession } from '../../hooks/useGameSession'
import { useActivitySession } from '../../hooks/useActivitySession'
import GameSetupScreen from '../GameSetupScreen'
import GameCompleteScreen from '../GameCompleteScreen'
import GamePlayHeader from '../GamePlayHeader'
import SaveErrorBanner from '../SaveErrorBanner'
import { useMobile } from '../../hooks/useMobile'
import { useSessionMode } from '../../professional/SessionModeContext'
import { formatGameTime } from '../utils'
import { COLORS, SHADOWS, RADIUS } from '../../styles/tokens'

const ACTIVITY_SLUG = 'crosswords'
const DIFFICULTIES: Difficulty[] = ['easy', 'medium', 'hard']

interface CrosswordsConfig { difficulty: Difficulty }
interface CrosswordsGameProps { onBack: () => void }
type GameScreen = 'setup' | 'playing' | 'complete'

export default function CrosswordsGame({ onBack }: CrosswordsGameProps) {
  const { t, language }  = useTranslation()
  const isMobile         = useMobile()
  const sessionMode      = useSessionMode()
  const { savedSession, saveConfig, clear } = useActivitySession<CrosswordsConfig>(ACTIVITY_SLUG, !sessionMode)
  const restored = savedSession?.config ?? null

  const [screen,     setScreen]     = useState<GameScreen>(sessionMode ? 'playing' : (restored ? 'playing' : 'setup'))
  const [difficulty, setDifficulty] = useState<Difficulty>((sessionMode?.config.difficulty as Difficulty) ?? restored?.difficulty ?? 'easy')
  const [wordPool,   setWordPool]   = useState<string[]>([])
  const [loading,    setLoading]    = useState(true)

  useEffect(() => {
    let cancelled = false
    activitiesApi.get(ACTIVITY_SLUG)
      .then(detail => {
        if (cancelled) return
        const d = detail.data as { esWords?: string[]; enWords?: string[] } | null
        const words = language === 'en' ? (d?.enWords ?? []) : (d?.esWords ?? [])
        setWordPool(words)
        setLoading(false)
      })
      .catch(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [language])

  const hook = useCrosswords(difficulty, wordPool)
  const { gameComplete, moves, startTime, endTime, puzzle, foundWordIds, hintsUsed, bonusWordsFound } = hook.state

  const sessionItemsResetDone = useRef(false)
  useEffect(() => {
    if (!sessionMode && !restored) return
    if (loading) return
    if (wordPool.length === 0) return
    if (sessionItemsResetDone.current) return
    sessionItemsResetDone.current = true
    hook.resetGame()
  }, [loading, wordPool, sessionMode, hook.resetGame]) // eslint-disable-line react-hooks/exhaustive-deps

  // Each hint costs ceil(totalLetters / totalWords) extra moves (proportional penalty)
  const hintMoveCost = puzzle
    ? Math.ceil(puzzle.letters.length / puzzle.placedWords.length)
    : 1
  const adjustedMoves = moves + hintsUsed * hintMoveCost

  const { sessionId } = useGameSession({
    activitySlug: ACTIVITY_SLUG,
    difficulty,
    enabled: screen === 'playing',
  })

  const { saveError, clearSaveError } = useGameCompletion({
    gameComplete:  gameComplete && (puzzle?.placedWords.length ?? 0) > 0,
    activitySlug:  ACTIVITY_SLUG,
    difficulty,
    moves:         adjustedMoves,
    startTime,
    endTime,
    pairsTotal:   puzzle?.placedWords.length ?? 0,
    pairsMatched: foundWordIds.size + bonusWordsFound.length,
    sessionId,
    onCompleted:  () => { clear(); setScreen('complete') },
  })

  const handleStart = () => {
    hook.resetGame()
    saveConfig({ difficulty })
    setScreen('playing')
  }

  const cw = t.crosswords

  if (loading) {
    return (
      <div style={s.loadingRoot}>
        {cw.loading}
      </div>
    )
  }

  if (!loading && !puzzle && screen === 'playing') {
    return (
      <div style={s.loadingRoot}>
        <p>{cw.generationFailed}</p>
        <button onClick={onBack} style={s.retryBtn}>{cw.backToDashboard}</button>
      </div>
    )
  }

  return (
    <div style={s.root}>
      {saveError && (
        <SaveErrorBanner message={t.common.saveError} dismissLabel={t.common.dismissError} onDismiss={clearSaveError} />
      )}

      {screen === 'setup' && (
        <SetupScreen
          difficulty={difficulty}
          onDifficultyChange={setDifficulty}
          onStart={handleStart}
          onBack={onBack}
          isMobile={isMobile}
        />
      )}

      {screen === 'playing' && puzzle && (
        <PlayScreen
          state={hook.state}
          puzzle={puzzle}
          onStartDrag={hook.startDrag}
          onExtendDrag={hook.extendDrag}
          onEndDrag={hook.endDrag}
          onCancelDrag={hook.cancelDrag}
          onRevealHint={hook.revealHint}
          onBack={sessionMode ? onBack : () => { clear(); setScreen('setup') }}
          isMobile={isMobile}
        />
      )}

      {screen === 'complete' && puzzle && (
        <GameCompleteScreen
          emoji="📝"
          title={cw.complete.title}
          subtitle={cw.complete.subtitle}
          stats={[
            { value: String(moves),                        label: cw.complete.movesLabel },
            { value: formatGameTime(startTime, endTime),   label: cw.complete.timeLabel },
            { value: cw.difficulty[difficulty].label,      label: cw.complete.levelLabel },
            ...(hintsUsed > 0
              ? [{ value: String(hintsUsed), label: cw.complete.hintsUsedLabel }]
              : []),
            ...(bonusWordsFound.length > 0
              ? [{ value: String(bonusWordsFound.length), label: cw.complete.bonusWordsLabel }]
              : []),
          ]}
          playAgainLabel={cw.complete.playAgain}
          changeLevelLabel={cw.complete.changeLevel}
          backLabel={cw.backToDashboard}
          onPlayAgain={() => { hook.resetGame(); saveConfig({ difficulty }); setScreen('playing') }}
          onChangeLevel={() => { clear(); setScreen('setup') }}
          onBack={onBack}
        />
      )}
    </div>
  )
}

/* ── Setup Screen ─────────────────────────────────────────────────── */

function SetupScreen({ difficulty, onDifficultyChange, onStart, onBack, isMobile }: {
  difficulty:         Difficulty
  onDifficultyChange: (d: Difficulty) => void
  onStart:            () => void
  onBack:             () => void
  isMobile:           boolean
}) {
  const { t } = useTranslation()
  const cw    = t.crosswords

  return (
    <GameSetupScreen
      title={cw.title}
      subtitle={cw.subtitle}
      backLabel={cw.backToDashboard}
      onBack={onBack}
      difficulties={DIFFICULTIES.map(id => ({ id, ...cw.difficulty[id] }))}
      selectedDifficulty={difficulty}
      onDifficultyChange={id => onDifficultyChange(id as Difficulty)}
      chooseDifficultyLabel={cw.chooseDifficultyTitle}
      startLabel={cw.startBtn}
      onStart={onStart}
    >
      <CrosswordPreview isMobile={isMobile} />
    </GameSetupScreen>
  )
}

function CrosswordPreview({ isMobile }: { isMobile: boolean }) {
  const cells = [
    [0,1,0,0], [1,1,1,1], [0,1,0,0], [0,1,0,0],
  ]
  const size = isMobile ? 28 : 32
  return (
    <div style={s.preview}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {cells.map((row, r) => (
          <div key={r} style={{ display: 'flex', gap: 4 }}>
            {row.map((filled, c) => (
              <div key={c} style={{
                width:           size,
                height:          size,
                backgroundColor: filled ? '#E8F5EE' : 'transparent',
                border:          filled ? `2px solid ${COLORS.greenLight}` : 'none',
                borderRadius:    4,
              }} />
            ))}
          </div>
        ))}
      </div>
      <div style={s.previewLetters}>
        {['A','M','O','R'].map(l => (
          <div key={l} style={s.previewLetter}>{l}</div>
        ))}
      </div>
    </div>
  )
}

/* ── Orientation + window size hooks ──────────────────────────────── */

function useIsLandscape(): boolean {
  const [landscape, setLandscape] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(orientation: landscape)').matches,
  )
  useEffect(() => {
    const mq = window.matchMedia('(orientation: landscape)')
    const handler = (e: MediaQueryListEvent) => setLandscape(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])
  return landscape
}

function useWindowSize() {
  const [size, setSize] = useState(() => ({
    w: typeof window !== 'undefined' ? window.innerWidth  : 800,
    h: typeof window !== 'undefined' ? window.innerHeight : 600,
  }))
  useEffect(() => {
    let t: ReturnType<typeof setTimeout>
    // Debounce: ignore transient viewport changes (address-bar show/hide on mobile)
    const handler = () => {
      clearTimeout(t)
      t = setTimeout(() => setSize({ w: window.innerWidth, h: window.innerHeight }), 150)
    }
    window.addEventListener('resize', handler)
    window.addEventListener('orientationchange', handler)
    return () => {
      clearTimeout(t)
      window.removeEventListener('resize', handler)
      window.removeEventListener('orientationchange', handler)
    }
  }, [])
  return size
}

/* ── Play Screen ──────────────────────────────────────────────────── */

function PlayScreen({ state, puzzle, onStartDrag, onExtendDrag, onEndDrag, onCancelDrag, onRevealHint, onBack, isMobile }: {
  state:          ReturnType<typeof useCrosswords>['state']
  puzzle:         CrosswordPuzzle
  onStartDrag:    (idx: number) => void
  onExtendDrag:   (idx: number) => void
  onEndDrag:      () => void
  onCancelDrag:   () => void
  onRevealHint:   () => void
  onBack:         () => void
  isMobile:       boolean
}) {
  const { t } = useTranslation()
  const cw    = t.crosswords
  const elapsed     = useTimer(state.startTime, state.endTime)
  const isLandscape = useIsLandscape()
  const { w: winW, h: winH } = useWindowSize()
  const [bonusOpen, setBonusOpen] = useState(false)

  // Landscape sizes: fill the available height, grow font automatically
  const HEADER_H = 72
  const availH   = winH - HEADER_H
  const gridGap  = 3
  // Grid: fit rows in ~90% of height and cols in ~44% of width
  const rawByH = Math.floor((availH * 0.90 + gridGap) / puzzle.rows) - gridGap
  const rawByW = Math.floor((winW  * 0.44 + gridGap) / puzzle.cols) - gridGap
  const landscapeCellSize = Math.min(60, Math.max(28, Math.min(rawByH, rawByW)))
  // Circle: cap at 320px so it doesn't overwhelm small landscape screens
  const circleMaxByH = availH - 132
  const circleMaxByW = winW * 0.42
  const landscapeCircleTarget = Math.max(180, Math.min(circleMaxByH, circleMaxByW, 320))

  const circleRef   = useRef<LetterCircleHandle>(null)
  const bonusBtnRef = useRef<HTMLButtonElement>(null)
  const ballRef     = useRef<HTMLDivElement>(null)

  // DOM-only animation: writes directly to ballRef.current.style — no setState in effect
  const launchBall = useCallback((word: string, sourceIdx: number) => {
    const letterRect = circleRef.current?.getLetterRect(sourceIdx)
    const buttonRect = bonusBtnRef.current?.getBoundingClientRect()
    const el = ballRef.current
    if (!letterRect || !buttonRect || !el) return
    const fromX = letterRect.left + letterRect.width  / 2
    const fromY = letterRect.top  + letterRect.height / 2
    const dx    = buttonRect.left + buttonRect.width  / 2 - fromX
    const dy    = buttonRect.top  + buttonRect.height / 2 - fromY
    el.textContent     = word
    el.style.left      = `${fromX}px`
    el.style.top       = `${fromY}px`
    el.style.transform = 'translate(-50%, -50%)'
    el.style.opacity   = '1'
    el.style.transition = 'none'
    el.style.display   = 'flex'
    // Two rAF frames to allow layout before starting transition
    requestAnimationFrame(() => requestAnimationFrame(() => {
      el.style.transform  = `translate(-50%, -50%) translate(${dx}px, ${dy}px) scale(0.75)`
      el.style.opacity    = '0'
      el.style.transition = 'transform 580ms cubic-bezier(0.34,1.3,0.64,1), opacity 220ms ease-in 380ms'
    }))
    setTimeout(() => { el.style.display = 'none'; el.style.transition = 'none' }, 750)
  }, [])

  useEffect(() => {
    const { lastBonusWord, bonusWordSourceIdx } = state
    if (!lastBonusWord || bonusWordSourceIdx === null) return
    launchBall(lastBonusWord, bonusWordSourceIdx)
  }, [state.lastBonusWord]) // eslint-disable-line react-hooks/exhaustive-deps

  const wordsFound = state.foundWordIds.size
  const totalWords = puzzle.placedWords.length
  const progress   = totalWords > 0 ? (wordsFound / totalWords) * 100 : 0
  const currentWord = state.dragPath.map(i => puzzle.letters[i]).join('')

  const instruction = state.lastBonusWord !== null
    ? `⭐ ${state.lastBonusWord}`
    : wordsFound === totalWords
      ? cw.instructionComplete
      : currentWord.length > 0
        ? currentWord
        : cw.instructionDrag

  const instructionColor = state.lastBonusWord !== null
    ? '#B45309'
    : currentWord.length > 0
      ? COLORS.green
      : '#666'

  const unhintedUnfoundCount = puzzle.placedWords
    .filter(pw => !state.foundWordIds.has(pw.id))
    .flatMap(pw => {
      const dr = pw.dir === 'V' ? 1 : 0
      const dc = pw.dir === 'H' ? 1 : 0
      return Array.from({ length: pw.word.length }, (_, i) => {
        const r = pw.row + puzzle.rowOffset + i * dr
        const c = pw.col + puzzle.colOffset + i * dc
        return `${r},${c}`
      })
    })
    .filter(k => !state.hintedCells.has(k)).length
  const hintsAvailable = unhintedUnfoundCount > 0 && !state.gameComplete
  const hasBonusWords  = state.bonusWordsFound.length > 0

  // Instruction + circle + controls — parameterised so landscape can pass a sized circle
  const makeInteractivePanel = (circleTarget?: number) => (
    <>
      <p style={{
        ...s.instruction,
        fontWeight: (currentWord.length > 0 || state.lastBonusWord !== null) ? 700 : 400,
        fontSize:   (currentWord.length > 0 || state.lastBonusWord !== null) ? 22 : 18,
        color:      instructionColor,
      }}>
        {instruction}
      </p>

      <LetterCircle
        ref={circleRef}
        letters={puzzle.letters}
        dragPath={state.dragPath}
        wrongWord={state.wrongWord}
        onStartDrag={onStartDrag}
        onExtendDrag={onExtendDrag}
        onEndDrag={onEndDrag}
        onCancelDrag={onCancelDrag}
        isMobile={isMobile}
        sizeTarget={circleTarget}
      />

      {bonusOpen && hasBonusWords && (
        <div style={s.bonusExpanded}>
          {state.bonusWordsFound.map(w => (
            <span key={w} style={s.bonusChip}>{w}</span>
          ))}
        </div>
      )}

      <div style={s.controlRow}>
        <button
          onClick={onRevealHint}
          disabled={!hintsAvailable}
          style={{
            ...s.hintBtn,
            opacity: hintsAvailable ? 1 : 0.4,
            cursor:  hintsAvailable ? 'pointer' : 'default',
          }}
        >
          {`💡 ${cw.hintBtn}${state.hintsUsed > 0 ? ` (${state.hintsUsed})` : ''}`}
        </button>
        {hasBonusWords && (
          <button
            ref={bonusBtnRef}
            onClick={() => setBonusOpen(o => !o)}
            style={s.bonusBtn}
          >
            {`⭐ ${cw.bonusWordsLabel} (${state.bonusWordsFound.length}) ${bonusOpen ? '▲' : '▼'}`}
          </button>
        )}
      </div>
    </>
  )

  const mainPadding = isLandscape
    ? '8px 16px 16px'
    : isMobile ? '12px 8px 24px' : '20px 24px 32px'

  // Landscape root needs definite height so children can fill it
  const rootStyle = isLandscape
    ? { height: '100dvh', overflow: 'hidden', display: 'flex', flexDirection: 'column' as const, backgroundColor: '#FAFAF7' }
    : s.playRoot

  return (
    <div style={rootStyle}>
      <GamePlayHeader
        title={cw.title}
        stats={[
          { value: `${wordsFound}/${totalWords}`, label: cw.wordsFound },
          { value: elapsed,                        label: cw.time },
        ]}
        progress={progress}
        progressNow={wordsFound}
        progressMax={totalWords}
        onBack={onBack}
        backLabel={cw.backToDashboard}
      />

      {isLandscape ? (
        <main style={{ ...s.playMain, ...s.playMainLandscape, padding: mainPadding }}>
          <div style={s.landscapeLeft}>
            <CrosswordGrid
              puzzle={puzzle}
              foundWordIds={state.foundWordIds}
              lastFoundWordId={state.lastFoundWordId}
              hintedCells={state.hintedCells}
              isMobile={isMobile}
              cellSizeOverride={landscapeCellSize}
            />
          </div>
          <div style={s.landscapeRight}>
            {makeInteractivePanel(landscapeCircleTarget)}
          </div>
        </main>
      ) : (
        <main style={{ ...s.playMain, padding: mainPadding }}>
          <CrosswordGrid
            puzzle={puzzle}
            foundWordIds={state.foundWordIds}
            lastFoundWordId={state.lastFoundWordId}
            hintedCells={state.hintedCells}
            isMobile={isMobile}
          />
          {makeInteractivePanel()}
        </main>
      )}

      {/* Flying ball animation: DOM-ref driven, no React state */}
      <div
        ref={ballRef}
        style={{
          display:         'none',
          position:        'fixed',
          left:            0,
          top:             0,
          transform:       'translate(-50%, -50%)',
          backgroundColor: '#FDE68A',
          color:           '#78350F',
          border:          '1.5px solid #F59E0B',
          borderRadius:    20,
          padding:         '4px 14px',
          fontSize:        15,
          fontWeight:      700,
          pointerEvents:   'none',
          zIndex:          9999,
          whiteSpace:      'nowrap',
          alignItems:      'center',
          justifyContent:  'center',
        }}
      />
    </div>
  )
}

/* ── Crossword grid ───────────────────────────────────────────────── */

function CrosswordGrid({ puzzle, foundWordIds, lastFoundWordId, hintedCells, isMobile, cellSizeOverride }: {
  puzzle:            CrosswordPuzzle
  foundWordIds:      Set<number>
  lastFoundWordId:   number | null
  hintedCells:       Set<string>
  isMobile:          boolean
  cellSizeOverride?: number
}) {
  const cellSize = cellSizeOverride ?? (isMobile ? 36 : 42)
  const gap      = 3

  // Build cell map: "row,col" -> { letter, wordIds }
  const cellMap = new Map<string, { letter: string; wordIds: Set<number> }>()
  for (const pw of puzzle.placedWords) {
    const dr = pw.dir === 'V' ? 1 : 0
    const dc = pw.dir === 'H' ? 1 : 0
    for (let i = 0; i < pw.word.length; i++) {
      const r = pw.row + puzzle.rowOffset + i * dr
      const c = pw.col + puzzle.colOffset + i * dc
      const k = `${r},${c}`
      const existing = cellMap.get(k)
      if (existing) {
        existing.wordIds.add(pw.id)
      } else {
        cellMap.set(k, { letter: pw.word[i], wordIds: new Set([pw.id]) })
      }
    }
  }

  const totalW = puzzle.cols * (cellSize + gap) - gap
  const totalH = puzzle.rows * (cellSize + gap) - gap

  return (
    <div style={s.gridWrapper}>
      <div style={{ position: 'relative', width: totalW, height: totalH }}>
        {Array.from(cellMap.entries()).map(([k, cell]) => {
          const [r, c] = k.split(',').map(Number)
          const isFound    = [...cell.wordIds].some(id => foundWordIds.has(id))
          const isFlashing = lastFoundWordId !== null && cell.wordIds.has(lastFoundWordId)
          const isHinted   = hintedCells.has(k) && !isFound
          const showLetter = isFound || isHinted

          const bgColor = isFlashing
            ? COLORS.greenLight
            : isFound
              ? COLORS.greenSurface
              : isHinted
                ? '#FEF3C7'   // amber-100
                : '#fff'
          const borderColor = isFlashing
            ? COLORS.greenLight
            : isFound
              ? COLORS.greenPale
              : isHinted
                ? '#F59E0B'   // amber-400
                : '#d0d0d0'
          const textColor = isFound
            ? COLORS.greenDark
            : isHinted
              ? '#92400E'   // amber-800
              : 'transparent'

          return (
            <div
              key={k}
              style={{
                position:        'absolute',
                left:            c * (cellSize + gap),
                top:             r * (cellSize + gap),
                width:           cellSize,
                height:          cellSize,
                backgroundColor: bgColor,
                border:          `2px solid ${borderColor}`,
                borderRadius:    6,
                display:         'flex',
                alignItems:      'center',
                justifyContent:  'center',
                fontSize:        cellSize * 0.48,
                fontWeight:      800,
                color:           textColor,
                transition:      'all 0.2s',
                boxShadow:       isFlashing ? SHADOWS.active : SHADOWS.subtle,
              }}
            >
              {showLetter ? cell.letter : ''}
            </div>
          )
        })}
      </div>
    </div>
  )
}

/* ── Letter circle ────────────────────────────────────────────────── */

interface LetterCircleHandle {
  getLetterRect: (idx: number) => DOMRect | null
}

interface LetterCircleProps {
  letters:      string[]
  dragPath:     number[]
  wrongWord:    boolean
  onStartDrag:  (idx: number) => void
  onExtendDrag: (idx: number) => void
  onEndDrag:    () => void
  onCancelDrag: () => void
  isMobile:     boolean
  sizeTarget?:  number
}

const LetterCircle = forwardRef<LetterCircleHandle, LetterCircleProps>(
function LetterCircle({ letters, dragPath, wrongWord, onStartDrag, onExtendDrag, onEndDrag, onCancelDrag, isMobile, sizeTarget }, ref) {
  const containerRef = useRef<HTMLDivElement>(null)
  const letterRefs   = useRef<(HTMLDivElement | null)[]>([])
  const isDragging   = useRef(false)

  useImperativeHandle(ref, () => ({
    getLetterRect: (idx: number) => letterRefs.current[idx]?.getBoundingClientRect() ?? null,
  }))

  // When sizeTarget provided, derive btnSize from it; otherwise use device defaults
  const btnSize = sizeTarget != null
    ? Math.round(Math.min(72, Math.max(48, sizeTarget * 0.24)))
    : (isMobile ? 52 : 56)
  const baseRadius = sizeTarget != null
    ? sizeTarget / 2 - btnSize / 2 - 8
    : (isMobile ? 88 : 100)
  // Ensure enough arc between buttons: each needs at least (btnSize + 12)px of arc
  const radius    = Math.max(baseRadius, Math.ceil(letters.length * (btnSize + 12) / (2 * Math.PI)))
  const totalSize = (radius + btnSize / 2 + 8) * 2
  // Font scales with button size (min 18px for accessibility)
  const fontSize  = Math.max(18, Math.round(btnSize * 0.40))

  function getLetterAtPoint(x: number, y: number): number | null {
    for (let i = 0; i < letters.length; i++) {
      const el = letterRefs.current[i]
      if (!el) continue
      const rect = el.getBoundingClientRect()
      if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) return i
    }
    return null
  }

  function handlePointerDown(e: React.PointerEvent) {
    const idx = getLetterAtPoint(e.clientX, e.clientY)
    if (idx === null) return
    isDragging.current = true
    onStartDrag(idx)
    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
    e.preventDefault()
  }

  function handlePointerMove(e: React.PointerEvent) {
    if (!isDragging.current) return
    const idx = getLetterAtPoint(e.clientX, e.clientY)
    if (idx !== null) onExtendDrag(idx)
  }

  function handlePointerUp() {
    if (!isDragging.current) return
    isDragging.current = false
    onEndDrag()
  }

  function handlePointerCancel() {
    isDragging.current = false
    onCancelDrag()
  }

  return (
    <div style={s.circleWrapper}>
      <div
        ref={containerRef}
        style={{
          position:    'relative',
          width:       totalSize,
          height:      totalSize,
          touchAction: 'none',
          userSelect:  'none',
        }}
        className={wrongWord ? 'wm-shake' : undefined}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
      >
        {/* SVG connector lines */}
        <svg
          style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
          width={totalSize}
          height={totalSize}
        >
          {dragPath.length >= 2 && dragPath.slice(0, -1).map((fromIdx, i) => {
            const toIdx = dragPath[i + 1]
            const fromAngle = (fromIdx / letters.length) * 2 * Math.PI - Math.PI / 2
            const toAngle   = (toIdx   / letters.length) * 2 * Math.PI - Math.PI / 2
            const cx = totalSize / 2
            const cy = totalSize / 2
            const x1 = cx + radius * Math.cos(fromAngle)
            const y1 = cy + radius * Math.sin(fromAngle)
            const x2 = cx + radius * Math.cos(toAngle)
            const y2 = cy + radius * Math.sin(toAngle)
            return (
              <line
                key={i}
                x1={x1} y1={y1} x2={x2} y2={y2}
                stroke={COLORS.greenLight}
                strokeWidth={4}
                strokeLinecap="round"
                opacity={0.7}
              />
            )
          })}
        </svg>

        {/* Letter buttons */}
        {letters.map((letter, i) => {
          const angle    = (i / letters.length) * 2 * Math.PI - Math.PI / 2
          const cx       = totalSize / 2
          const cy       = totalSize / 2
          const x        = cx + radius * Math.cos(angle) - btnSize / 2
          const y        = cy + radius * Math.sin(angle) - btnSize / 2
          const isActive = dragPath.includes(i)
          const isLast   = dragPath[dragPath.length - 1] === i

          return (
            <div
              key={i}
              ref={el => { letterRefs.current[i] = el }}
              style={{
                position:        'absolute',
                left:            x,
                top:             y,
                width:           btnSize,
                height:          btnSize,
                borderRadius:    RADIUS.round,
                backgroundColor: isLast
                  ? COLORS.green
                  : isActive
                    ? COLORS.greenLight
                    : '#fff',
                border:          `2px solid ${isActive ? COLORS.green : '#ddd'}`,
                display:         'flex',
                alignItems:      'center',
                justifyContent:  'center',
                fontSize:        fontSize,
                fontWeight:      800,
                color:           isActive ? '#fff' : COLORS.greenDark,
                boxShadow:       isActive ? SHADOWS.active : SHADOWS.card,
                transition:      'background-color 0.1s, transform 0.1s',
                transform:       isLast ? 'scale(1.15)' : 'scale(1)',
                cursor:          'pointer',
              }}
            >
              {letter}
            </div>
          )
        })}
      </div>
    </div>
  )
})

/* ── Styles ───────────────────────────────────────────────────────── */

const s: Record<string, React.CSSProperties> = {
  root: {
    minHeight:       '100dvh',
    backgroundColor: '#FAFAF7',
  },
  loadingRoot: {
    display:         'flex',
    flexDirection:   'column',
    alignItems:      'center',
    justifyContent:  'center',
    minHeight:       '100dvh',
    fontSize:        22,
    color:           COLORS.textBody,
    gap:             20,
    padding:         24,
    textAlign:       'center',
  },
  retryBtn: {
    minHeight:       48,
    padding:         '0 32px',
    fontSize:        18,
    fontWeight:      700,
    backgroundColor: COLORS.greenLight,
    color:           '#fff',
    border:          'none',
    borderRadius:    RADIUS.md,
    cursor:          'pointer',
  },
  playRoot: {
    display:         'flex',
    flexDirection:   'column',
    minHeight:       '100dvh',
    backgroundColor: '#FAFAF7',
  },
  playMain: {
    flex:            1,
    display:         'flex',
    flexDirection:   'column',
    alignItems:      'center',
    gap:             20,
  },
  gridWrapper: {
    width:           '100%',
    display:         'flex',
    justifyContent:  'center',
    padding:         '8px 0',
  },
  instruction: {
    fontSize:        18,
    color:           '#666',
    fontStyle:       'italic',
    margin:          0,
    textAlign:       'center',
    minHeight:       28,
    letterSpacing:   0.5,
  },
  circleWrapper: {
    display:         'flex',
    justifyContent:  'center',
    alignItems:      'center',
    padding:         '8px 0',
  },
  // Landscape two-column layout
  playMainLandscape: {
    flexDirection:   'row' as const,
    // stretch: both columns always full height — prevents re-centering the grid
    // when instruction text size changes on letter select
    alignItems:      'stretch',
    justifyContent:  'center',
    gap:             24,
    overflow:        'hidden',
  },
  landscapeLeft: {
    flex:            '0 0 auto',
    display:         'flex',
    alignItems:      'center',
    justifyContent:  'center',
  },
  landscapeRight: {
    flex:            '0 0 auto',
    display:         'flex',
    flexDirection:   'column' as const,
    alignItems:      'center',
    justifyContent:  'center',
    gap:             12,
  },
  // Control row: hint + bonus buttons side by side
  controlRow: {
    display:         'flex',
    flexDirection:   'row' as const,
    gap:             8,
    flexWrap:        'wrap' as const,
    justifyContent:  'center',
  },
  hintBtn: {
    minHeight:       48,
    padding:         '0 20px',
    fontSize:        17,
    fontWeight:      700,
    backgroundColor: '#FEF3C7',
    color:           '#92400E',
    border:          '2px solid #F59E0B',
    borderRadius:    RADIUS.md,
    cursor:          'pointer',
  },
  bonusBtn: {
    minHeight:       48,
    padding:         '0 20px',
    fontSize:        17,
    fontWeight:      700,
    backgroundColor: '#FEF3C7',
    color:           '#92400E',
    border:          '2px solid #F59E0B',
    borderRadius:    RADIUS.md,
    cursor:          'pointer',
  },
  // Bonus words expanded chip list (appears above control row)
  bonusExpanded: {
    display:         'flex',
    flexWrap:        'wrap' as const,
    gap:             6,
    justifyContent:  'center',
    padding:         '8px 12px',
    backgroundColor: '#FFFBEB',
    border:          '1.5px solid #F59E0B',
    borderRadius:    RADIUS.md,
    maxWidth:        400,
  },
  bonusChip: {
    backgroundColor: '#FDE68A',
    color:           '#78350F',
    borderRadius:    20,
    padding:         '3px 12px',
    fontSize:        15,
    fontWeight:      700,
    border:          '1px solid #F59E0B',
  },
  // Setup preview
  preview: {
    display:         'flex',
    flexDirection:   'column',
    alignItems:      'center',
    gap:             16,
    backgroundColor: '#F5F9F6',
    borderRadius:    RADIUS.lg,
    padding:         '20px 24px',
    margin:          '8px 0',
  },
  previewLetters: {
    display:         'flex',
    gap:             8,
  },
  previewLetter: {
    width:           40,
    height:          40,
    borderRadius:    RADIUS.round,
    backgroundColor: '#fff',
    border:          `2px solid ${COLORS.greenPale}`,
    display:         'flex',
    alignItems:      'center',
    justifyContent:  'center',
    fontSize:        18,
    fontWeight:      800,
    color:           COLORS.greenDark,
    boxShadow:       SHADOWS.subtle,
  },
}
